import React, { useMemo, useCallback } from 'react';
import { Row, Col, Button } from 'antd';
import {
  ToolOutlined,
  PlusCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import { ReactSortable } from 'react-sortablejs';

import Parts from './Parts';
import { AddsContainer } from './common';
import {
  createHandleDragEnd,
  onlyAllowType,
} from '../util/CreateHandleDragEnd';
import EmptyItem from './EmptyItem';
import { v4 as uuidv4 } from 'uuid';

const MemoizedEmptyItem = React.memo(EmptyItem);

export const PartsContainer = React.memo(function PartsContainer({
  itemKey,
  parts,
  addSubItem,
  deleteSubItem,
  changeSubItem,
  moveItem,
}) {
  const handleDragEnd = useMemo(
    () =>
      createHandleDragEnd({
        items: parts,
        type: 'parts',
        itemKey,
        moveItem,
      }),
    [parts, itemKey, moveItem]
  );

  const sortedParts = useMemo(
    () =>
      [...(parts || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [parts]
  );

  const handleAddPart = useCallback(() => {
    const maxPosition = sortedParts.reduce(
      (max, part) => Math.max(max, part.position ?? 0),
      0
    );

    addSubItem('parts', {
      key: uuidv4(),
      id: _.random(100000),
      id_vehicle_part: '',
      amount: 1,
      volumeunit: '',
      originalAmount: 1,
      markup: 0,
      cost: 0,
      position: maxPosition + 1,
    });
  }, [addSubItem, sortedParts]);

  const setListGuardRef = React.useRef({});

  return (
    <>
      <Row
        style={{ margin: '10px' }}
        gutter={16}
        data-lineitem={itemKey.toString()}
      >
        <Col span={24}>
          <AddsContainer>
            <Row
              style={{
                alignItems: 'flex-start',
                marginBottom: 8,
                cursor: 'grab',
              }}
              gutter={16}
            >
              <Col
                style={{
                  width: 40,
                  textAlign: 'center',
                  paddingTop: '25px',
                }}
              >
                <ToolOutlined style={{ fontSize: '32px' }} />
              </Col>
              <Col style={{ flex: 1, paddingLeft: 5 }}>
                <Row>
                  <Col span={11} style={{ borderBottom: '1px solid #d9d9d9' }}>
                    Parts:
                  </Col>
                  <Col span={2} style={{ borderBottom: '1px solid #d9d9d9' }}>
                    Quantity:
                  </Col>
                  <Col
                    span={3}
                    style={{
                      paddingLeft: '10px',
                      borderBottom: '1px solid #d9d9d9',
                    }}
                  >
                    Meas. Unit:
                  </Col>
                  <Col
                    span={2}
                    style={{
                      paddingLeft: '10px',
                      borderBottom: '1px solid #d9d9d9',
                    }}
                  >
                    Cost:
                  </Col>
                  <Col
                    span={2}
                    style={{
                      paddingLeft: '10px',
                      borderBottom: '1px solid #d9d9d9',
                    }}
                  >
                    Markup:
                  </Col>
                  <Col
                    span={3}
                    style={{
                      paddingLeft: '10px',
                      borderBottom: '1px solid #d9d9d9',
                    }}
                  >
                    Amount:
                  </Col>
                  <Col span={1}></Col>
                </Row>

                <ReactSortable
                  tag="div"
                  list={parts || []}
                  setList={(newList) => {
                    if (!setListGuardRef.current[itemKey]) {
                      setListGuardRef.current[itemKey] = true;
                      return;
                    }
                    moveItem(itemKey, 'parts', newList);
                  }}
                  onEnd={handleDragEnd}
                  group={{
                    name: 'sharedParts',
                    pull: true,
                    put: onlyAllowType('parts'),
                  }}
                  animation={150}
                  data-lineitem={itemKey.toString()}
                  scroll={true}
                  scrollSensitivity={100}
                  scrollSpeed={10}
                  forceFallback={true}
                  scrollTarget={() => document.querySelector('.wrapper-wo')}
                  ghostClass="ghost"
                  chosenClass="chosen"
                  dragClass="dragging"
                  tagProps={{
                    'data-lineitem': itemKey,
                    role: 'application',
                    'aria-label':
                      'Parts list. Items can be reordered by dragging and dropping.',
                    'aria-dropeffect': 'move',
                  }}
                >
                  {sortedParts.length === 0 ? (
                    <MemoizedEmptyItem message="Drag and drop part items here or add manually" />
                  ) : (
                    sortedParts.map((item) => (
                      <div
                        key={item.key}
                        data-id={item.key.toString()}
                        data-type="parts"
                        role="listitem"
                        aria-grabbed="false"
                        tabIndex={0}
                        aria-label={`Part: ${item.description || 'Unnamed part'}`}
                      >
                        <Parts
                          item={item}
                          woLineItemKey={itemKey}
                          deleteSubItem={() =>
                            deleteSubItem(itemKey, 'parts', item.key)
                          }
                          changeSubItem={(changes) =>
                            changeSubItem(itemKey, 'parts', item.key, {
                              ...changes,
                              position: item.position,
                            })
                          }
                        />
                      </div>
                    ))
                  )}
                </ReactSortable>
              </Col>
            </Row>
          </AddsContainer>

          <Row>
            <Col
              style={{ width: 40, textAlign: 'center', marginTop: '20px' }}
            />
            <Col style={{ flex: 1 }}>
              <Button
                type="link"
                style={{ paddingLeft: '12px' }}
                onClick={handleAddPart}
                icon={<PlusCircleOutlined />}
                data-testid="parts-container-add-part-button"
              >
                Add Part
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
});

export default PartsContainer;
